import { RealtimeClient } from "./RealtimeClient";
import { RestClient } from "./restClient";

export class UnitService {
	constructor(options) {
		this._options = options;
		this._rc = new RealtimeClient();
		this._restClient = new RestClient();
	}

	/**
	 * streamUnits
	 * stream all units and subsequent changes
	 */
	streamUnits(callback) {
		const args = {};
		return this._rc.subscribe("units-app", "/units", args, (msg) => {
			const response = JSON.parse(msg);
			if (response && callback) {
				callback(null, response);
			}
		});
	}

	/**
	 * getAllUnits
	 * returns all available units
	 */
	subscribeFeed(feedId, callback) {
		return this.subscribeFilteredFeed("system", feedId, null, callback);
	}

	/**
	 * getAllUnits
	 * returns all available units
	 */
	getAllUnits(callback) {
		const url = "/units-app/api/units";
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getUnitById
	 * returns unit matching given unitId
	 * @param unitId
	 */
	getUnitById(unitId, callback) {
		const url = `/units-app/api/units/${unitId}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getUnitMembers
	 * returns unit members assigned to unit matching given unitId
	 * @param unitId
	 */
	getUnitMembers(unitId, callback) {
		const url = `/units-app/api/units/${unitId}/unitMembers`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * createUnit
	 * create a new unit
	 * @param name - string
	 * @param unitMembers - string array of userMemberIds and/or entity objects
	 * TODO: it's possible or even likely unit members in this case will be an entity that we need to default a unitMember from
	 * so given that this needs to be either a string unitMemberId or an object made up of { feedId, entityId, entityType }
	 * if it is the latter on the backend we will create a new unitMember with defaults
	 */
	createUnit(name, unitMembers, callback) {
		const url = "/units-app/api/units";
		const body = JSON.stringify({ name, unitMembers });
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * renameUnit
	 * returns updated unit
	 * @param unitId
	 * @param name
	 */
	renameUnit(unitId, name, callback) {
		const url = `/units-app/api/units/${unitId}/rename`;
		const body = JSON.stringify({ name });
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * setUnitStatus
	 * returns updated unit
	 * @param unitId
	 * @param status - string (id from unitStatusTypes)
	 */
	setUnitStatus(unitId, status, callback) {
		const url = `/units-app/api/units/${unitId}/status`;
		const body = JSON.stringify({ status });
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * activateUnit
	 * returns updated unit
	 * @param unitId
	 */
	activateUnit(unitId, callback) {
		const url = `/units-app/api/units/${unitId}/activate`;
		this._restClient.exec_put(url, null, callback);
	}

	/**
	 * deactivateUnit
	 * returns updated unit
	 * @param unitId
	 */
	deactivateUnit(unitId, callback) {
		const url = `/units-app/api/units/${unitId}/deactivate`;
		this._restClient.exec_put(url, null, callback);
	}

	/**
	 * deleteUnit
	 * returns deleted unit
	 * @param unitId
	 */
	deleteUnit(unitId, callback) {
		const url = `/units-app/api/units/${unitId}`;
		this._restClient.exec_delete(url, callback);
	}

	/**
	 * streamUnitMembers
	 * stream all unitMembers and subsequent changes
	 */
	streamUnitMembers(callback) {
		const args = {};
		return this._rc.subscribe("units-app", "/unitMembers", args, (msg) => {
			const response = JSON.parse(msg);
			if (response && callback) {
				callback(null, response);
			}
		});
	}

	/**
	 * getAllUnitMembers
	 * returns all available unitMembers
	 */
	getAllUnitMembers(callback) {
		const url = "/units-app/api/unitMembers";
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getUnitMemberById
	 * returns unitMember matching given id
	 * @param unitMemberId
	 */
	getUnitMemberById(unitMemberId, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * createUnitMember
	 * returns unitMember object
	 * @param memberType - string "person" | "vehicle"
	 * @param feedId
	 * @param entityId
	 * @param entityType
	 * @param unitId
	 * @param phone
	 * @param roles
	 */
	createUnitMember(memberType, feedId, entityId, entityType, unitId, phone, roles, callback) {
		const url = "/units-app/api/unitMembers";
		const body = JSON.stringify({
			memberType,
			feedId,
			entityId,
			entityType,
			unitId,
			phone,
			roles
		});
		this._restClient.exec_post(url, body, callback);
	}

	/**
	 * updateUnitMember
	 * return unitMember object
	 * @param unitMemberId
	 * @param memberType - string "person" | "vehicle"
	 * @param feedId
	 * @param entityId
	 * @param entityType
	 * @param unitId
	 * @param phone
	 * @param roles
	 */
	updateUnitMember(unitMemberId, memberType, feedId, entityId, entityType, unitId, phone, roles, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}`;
		const body = JSON.stringify({
			memberType,
			feedId,
			entityId,
			entityType,
			unitId,
			phone,
			roles
		});
		this._restClient.exec_put(url, body, callback);
	}

	/**
	 * assignUnitToUnitMember
	 * returns updated unitMember
	 * @param unitMemberId
	 * @param unitId
	 */
	assignUnitToUnitMember(unitMemberId, unitId, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}/unit/${unitId}`;
		this._restClient.exec_put(url, null, callback);
	}

	/**
	 * unassignUnitFromUnitMember
	 * returns updated unitMember
	 * @param unitMemberId
	 * @param unitId
	 */
	unassignUnitFromUnitMember(unitMemberId, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}/unit`;
		this._restClient.exec_delete(url, callback);
	}

	/**
	 * activateUnitMember
	 * returns updated unitMember
	 * @param unitMemberId
	 */
	activateUnitMember(unitMemberId, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}/activate`;
		this._restClient.exec_put(url, null, callback);
	}

	/**
	 * deactivateUnitMember
	 * returns updated unitMember
	 * @param unitMemberId
	 */
	deactivateUnitMember(unitMemberId, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}/deactivate`;
		this._restClient.exec_put(url, null, callback);
	}

	/**
	 * deleteUnitMember
	 * returns deleted unitMember
	 * @param unitMemberId
	 */
	deleteUnitMember(unitMemberId, callback) {
		const url = `/units-app/api/unitMembers/${unitMemberId}`;
		this._restClient.exec_delete(url, callback);
	}

	/**
	 * getUnitStatusTypes
	 * returns array of unit status types
	 */
	getUnitStatusTypes(callback) {
		const url = "/units-app/api/unitStatusTypes";
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getUnitStatusTypes
	 * returns array of unit member role types
	 */
	getUnitMemberRoleTypes(callback) {
		const url = "/units-app/api/unitMemberRoleTypes";
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getAllSettings
	 * returns array of settings (kv pairs)
	 */
	getAllSettings(callback) {
		const url = "/settings";
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getSettingByKey
	 * returns setting value matching given key
	 */
	getSettingByKey(key, callback) {
		const url = `/settings/${key}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * getAppSettingsByKey
	 * returns app settings value matching given key
	 */

	getAppSettingsByKey(appId, key, callback) {
		const url = `/ecosystem/api/appSettings/${appId}/${key}`;
		this._restClient.exec_get(url, callback);
	}

	/**
	 * updateAppSettingsByKey
	 * adds or updates appSettings
	 */

	updateAppSettingsByKey(appId, key, value, callback) {
		const url = `/ecosystem/api/appSettings/${appId}/${key}`;
		const body = JSON.stringify({ value });
		this._restClient.exec_put(url, body, callback);
	}
}
